import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Trophy, Plus, ChevronDown, ChevronUp, Calendar, Clock, Medal, X, History, Users } from 'lucide-react';
import './App.css';
import { storeUser, clearAuth, getStoredUser, apiFetch, getClubKey, getAccessToken, isAccessTokenValid, AUTH_EXPIRED_EVENT } from './utils/api';
import Navigation from './components/Navigation';
import SessionForm from './components/SessionForm';
import GamesLibrary from './components/GamesLibrary';
import AdminPanel from './components/AdminPanel';
import ScoringSettings from './components/ScoringSettings';
import ResultsMaintenance from './components/ResultsMaintenance';
import SuggestionBox from './components/SuggestionBox';
import SessionDetailOverlay from './components/SessionDetailOverlay';
import NextGamePrediction from './components/NextGamePrediction';
import AdminSettings from './components/AdminSettings';
import ChatInterface from './components/ChatInterface';
import TerminalGame from './components/TerminalGame';
import Statistics from './components/Statistics';
import EventScheduler from './components/EventScheduler';
import SmartRecommendations from './components/SmartRecommendations';
import AdsAdmin from './components/AdsAdmin';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import SuperAdminAuth from './components/SuperAdminAuth';
import LandingPage from './components/LandingPage';
import OnboardingWizard from './components/OnboardingWizard';
import ClubSettings from './components/ClubSettings';
import { mapSharedVideoPlaylist } from './utils/mediaUrls';
import { fetchClubBetaFeatures, isBetaEnabled } from './utils/betaFeatures';
import {
  getOrderedLeaderboardMetrics,
  sortLeaderboardRows,
  ensureValidLeaderboardSort
} from './utils/leaderboardSettings';

// Icon component for PNG images (similar to Lucide icons)
const Icon = ({ src, alt = '', size = 16, className = '' }) => (
  <img 
    src={src} 
    alt={alt} 
    style={{ width: `${size}px`, height: `${size}px` }}
    className={`icon ${className}`}
  />
);

// --- SEASON HELPER ---
function leaderboardMetricMobileClass(metricIndex) {
  if (metricIndex === 0) return 'hide-mobile-1';
  if (metricIndex === 1) return 'hide-mobile-2';
  if (metricIndex === 2) return 'hide-mobile-3';
  return '';
}

const VIDEO_PLAYLIST = [
  '/intro.mp4',
  '/caylus.mp4',
  '/Puerto_Rico.mp4',
  '/Wonders.mp4',
  '/Dominion.mp4',
  '/Dead_of_Winter.mp4',
  '/New_York.mp4',
  '/Village.mp4',
  '/Small_World.mp4',
  '/Flamme_Rouge.mp4',
  '/Captain_Sonar.mp4',
  '/Paleo.mp4',
  '/El_Grande.mp4',
  '/Wonderful_World.mp4',
  '/Power_Grid.mp4'
];

/** Short label for UI (e.g. "2015-2016") from Seasons row */
const getSeasonLabel = (season) => {
  if (!season) return '';
  if (season.name) {
    return String(season.name).replace(/^Kausi\s+/i, '').trim();
  }
  return String(season.id);
};

/** Pick season covering today, else newest by season_start */
const pickCurrentSeason = (seasons) => {
  if (!Array.isArray(seasons) || seasons.length === 0) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const covering = seasons.find((s) => {
    const start = new Date(s.season_start);
    const end = new Date(s.season_end);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  });
  return covering || seasons[0];
};

const DEFAULT_PRIMARY = '#2c3e50';

/** Apply club primary color to CSS --primary (navbar etc.) */
const applyClubTheme = (club) => {
  const color = club?.primary_color || DEFAULT_PRIMARY;
  document.documentElement.style.setProperty('--primary', color);
};

const resetClubTheme = () => {
  document.documentElement.style.setProperty('--primary', DEFAULT_PRIMARY);
};


// --- LOCATION TRACKER ---
const LocationTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Save current path to localStorage (except login paths)
    if (location.pathname && !location.pathname.includes('token=')) {
      localStorage.setItem('masaklubi_lastPath', location.pathname);
    }
  }, [location.pathname]);
  
  return null;
};

// --- INITIAL REDIRECT ---
const InitialRedirect = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Only redirect if we're on the root path
    if (location.pathname === '/') {
      const lastPath = localStorage.getItem('masaklubi_lastPath');
      if (lastPath && lastPath !== '/') {
        // Check if the path is an admin path and user lacks access
        const isAdminPath = lastPath.startsWith('/admin');
        const isFullAdmin = user?.role === 'admin' || user?.role === 'superadmin';
        const isBoardAllowedPath =
          user?.role === 'board' &&
          (lastPath === '/admin' || lastPath.startsWith('/admin/results'));
        
        // Only redirect if user has access to the path
        if (!isAdminPath || isFullAdmin || isBoardAllowedPath) {
          navigate(lastPath, { replace: true });
        }
      }
    }
  }, []); // Run once on mount
  
  return null;
};

// --- SUPERADMIN PROTECTED WRAPPER ---
// Requires 2FA verification before showing SuperAdmin dashboard
const SuperAdminProtected = ({ user, onLoginAsClub }) => {
  const [verified, setVerified] = useState(false);
  const [showAuth, setShowAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already verified in this session
    // Token is stored in masaklubi_access_token (standard location)
    const isVerified = localStorage.getItem('masaklubi_superadmin_verified') === 'true';
    const hasToken = localStorage.getItem('masaklubi_access_token');
    
    // Also verify the user is actually a superadmin
    if (isVerified && hasToken && user?.role === 'superadmin') {
      setVerified(true);
      setShowAuth(false);
    } else {
      // If user changed or token missing, reset verification
      setVerified(false);
      setShowAuth(true);
    }
  }, [user]);

  const handleAuthenticated = (data) => {
    setVerified(true);
    setShowAuth(false);
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (!verified && showAuth) {
    return (
      <SuperAdminAuth 
        user={user} 
        onAuthenticated={handleAuthenticated}
        onCancel={handleCancel}
      />
    );
  }

  return <SuperAdminDashboard user={user} onLoginAsClub={onLoginAsClub} />;
};

// --- MAGIC LINK AUTH (single place - prevents /api/login loops) ---
const MAGIC_LINK_SESSION_KEY = 'masaklubi_magic_link_done';

const MagicLinkAuth = ({ onLogin, onClearUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!token) {
      sessionStorage.removeItem(MAGIC_LINK_SESSION_KEY);
      return;
    }

    // Already completed this token in this browser tab (survives remounts)
    if (sessionStorage.getItem(MAGIC_LINK_SESSION_KEY) === token) {
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      setSearchParams(next, { replace: true });
      return;
    }

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setProcessing(true);
    setError(null);

    // Clear prior session in storage + React state without remounting a second login path
    if (onClearUser) onClearUser();
    else clearAuth();

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || data.message || 'Login failed');
        }
        if (cancelled) return;

        sessionStorage.setItem(MAGIC_LINK_SESSION_KEY, token);
        const next = new URLSearchParams(searchParams);
        next.delete('token');
        setSearchParams(next, { replace: true });
        onLogin(data);
      } catch (err) {
        console.error('[MagicLinkAuth]', err);
        if (!cancelled) {
          sessionStorage.removeItem(MAGIC_LINK_SESSION_KEY);
          setError(err.message || 'Virheellinen tai vanhentunut linkki');
          const next = new URLSearchParams(searchParams);
          next.delete('token');
          setSearchParams(next, { replace: true });
        }
      } finally {
        inFlightRef.current = false;
        if (!cancelled) setProcessing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally only re-run when token changes (not onLogin identity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) {
    return (
      <div className="loading" style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'rgba(0,0,0,0.85)', zIndex: 99999,
        color: '#fff', flexDirection: 'column', gap: '1rem', padding: '1.5rem', textAlign: 'center'
      }}>
        <p>{String(error).includes('Too many login')
          ? 'Liian monta kirjautumisyritystä. Odota hetki ja kokeile uudelleen.'
          : 'Virheellinen tai vanhentunut linkki!'}
        </p>
        <button type="button" className="btn-primary" onClick={() => setError(null)}>
          Sulje
        </button>
      </div>
    );
  }

  if (processing || token) {
    return (
      <div className="loading" style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'rgba(0,0,0,0.8)', zIndex: 99999,
        color: '#fff', fontSize: '1.2rem'
      }}>
        Kirjaudutaan...
      </div>
    );
  }

  return null;
};

// --- HOME OR LOGIN (token handled by MagicLinkAuth) ---
const HomeOrLogin = () => {
  return <LandingPage />;
};

// --- DASHBOARD COMPONENT ---
const Dashboard = ({ user }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicConfig, setPublicConfig] = useState({ mediaBlobBaseUrl: '' });
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [gameNightsEnabled, setGameNightsEnabled] = useState(false);
  const [leaderboardSettings, setLeaderboardSettings] = useState(() =>
    ensureValidLeaderboardSort({
      visibleMetrics: ['time', 'points'],
      sortBy: 'points',
      sortDirection: 'desc'
    })
  );
  
  // New state for features
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [playerSessions, setPlayerSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingPlayerSessions, setLoadingPlayerSessions] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  // Season selector: Seasons table rows ({ id, name, season_start, season_end, ... })
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [showSeasonSelector, setShowSeasonSelector] = useState(false);

  useEffect(() => {
    fetch('/api/public-config')
      .then(res => res.ok ? res.json() : { mediaBlobBaseUrl: '' })
      .then(config => setPublicConfig(config || { mediaBlobBaseUrl: '' }))
      .catch(err => console.warn('Failed to load public config:', err));
  }, []);
  
  // Future Mode state (AI prediction visualization)
  const [futureMode, setFutureMode] = useState(false);
  const [futurePrediction, setFuturePrediction] = useState(null);
  const [loadingFuture, setLoadingFuture] = useState(false);
  
  // Video ref for loop control
  const videoRef = useRef(null);
  const [heroCollapsed, setHeroCollapsed] = useState(() =>
    localStorage.getItem('masaklubi_hero_collapsed') === '1'
  );
  
  const videoPlaylist = mapSharedVideoPlaylist(VIDEO_PLAYLIST, publicConfig.mediaBlobBaseUrl);

  // Start with a random video
  const [currentVideoIndex, setCurrentVideoIndex] = useState(() => 
    Math.floor(Math.random() * VIDEO_PLAYLIST.length)
  );

  const toggleHeroCollapsed = (e) => {
    e.stopPropagation();
    setHeroCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('masaklubi_hero_collapsed', next ? '1' : '0');
      return next;
    });
  };

  useEffect(() => {
    if (heroCollapsed && videoRef.current) {
      videoRef.current.pause();
    } else if (!heroCollapsed && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [heroCollapsed]);

  const selectedSeasonMeta = availableSeasons.find((s) => s.id === selectedSeasonId) || null;
  const currentSeasonMeta = pickCurrentSeason(availableSeasons);
  const isCurrentSeason =
    selectedSeasonId != null &&
    currentSeasonMeta != null &&
    selectedSeasonId === currentSeasonMeta.id;
  const selectedSeasonLabel = getSeasonLabel(selectedSeasonMeta);

  // Load seasons from Seasons table (authoritative season_id)
  const loadAvailableSeasons = async () => {
    try {
      const res = await apiFetch('/api/seasons');
      if (!res.ok) {
        console.warn('[App] Seasons fetch failed:', res.status);
        setAvailableSeasons([]);
        setSelectedSeasonId(null);
        return;
      }
      const seasons = await res.json();
      if (!Array.isArray(seasons)) {
        console.warn('[App] Seasons response is not an array');
        setAvailableSeasons([]);
        setSelectedSeasonId(null);
        return;
      }
      setAvailableSeasons(seasons);
      setSelectedSeasonId((prev) => {
        if (prev != null && seasons.some((s) => s.id === prev)) return prev;
        const current = pickCurrentSeason(seasons);
        return current ? current.id : null;
      });
    } catch (err) {
      console.error('Failed to load seasons:', err);
      setAvailableSeasons([]);
      setSelectedSeasonId(null);
    }
  };

  const loadData = () => {
    if (selectedSeasonId == null) return;
    setLoading(true);
    Promise.all([
      apiFetch(`/api/leaderboard?seasonId=${selectedSeasonId}`).then(res => res.ok ? res.json() : []),
      apiFetch('/api/club/leaderboard-settings').then(res => res.ok ? res.json() : null),
      apiFetch(`/api/season-games?seasonId=${selectedSeasonId}`).then(res => res.ok ? res.json() : []),
      apiFetch('/api/games').then(res => res.ok ? res.json() : []),
      apiFetch('/api/users').then(res => res.ok ? res.json() : []),
      fetchClubBetaFeatures().then((features) => {
        const enabled = isBetaEnabled(features, 'game_nights');
        setGameNightsEnabled(enabled);
        if (!enabled) return [];
        return apiFetch('/api/events?upcoming=true').then(res => res.ok ? res.json() : []);
      })
    ])
    .then(([lbData, lbSettings, seasonGamesData, gamesData, userData, eventsData]) => {
      setLeaderboard(lbData);
      if (lbSettings && Array.isArray(lbSettings.visibleMetrics)) {
        setLeaderboardSettings(
          ensureValidLeaderboardSort({
            visibleMetrics: lbSettings.visibleMetrics,
            sortBy: lbSettings.sortBy,
            sortDirection: lbSettings.sortDirection
          })
        );
      }
      setRecentGames(seasonGamesData);
      setGames(gamesData);
      setUsers(userData);
      setUpcomingEvents(Array.isArray(eventsData) ? eventsData : []);
    })
    .catch(err => console.error('[App] Data load error:', err))
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Only load seasons if user is logged in and has club access
    if (user && getClubKey()) {
      loadAvailableSeasons();
    }
  }, [user]);

  useEffect(() => {
    // Only load data when selected season id is set AND user has club access
    if (selectedSeasonId != null && user && getClubKey()) {
      loadData();
    }
  }, [selectedSeasonId, user]); // Reload when season changes or user logs in

  // Handle season change
  const handleSeasonChange = (seasonId) => {
    setSelectedSeasonId(seasonId);
    setShowSeasonSelector(false);
    setExpandedPlayer(null); // Reset expanded player
    setPlayerSessions([]);
  };

  // Video loop logic: play once, wait 60s, play random video from playlist
  const handleVideoEnded = () => {
    setTimeout(() => {
      if (videoRef.current) {
        // Select random video (different from current if possible)
        let nextIndex;
        if (videoPlaylist.length > 1) {
          do {
            nextIndex = Math.floor(Math.random() * videoPlaylist.length);
          } while (nextIndex === currentVideoIndex);
        } else {
          nextIndex = 0;
        }
        
        setCurrentVideoIndex(nextIndex);
        videoRef.current.src = videoPlaylist[nextIndex];
        videoRef.current.load();
        videoRef.current.play();
      }
    }, 2000); // 2 seconds pause between videos
  };

  // Handle video fullscreen on click (supports desktop + mobile)
  const handleVideoFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    // Check if already in fullscreen - exit if so
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return;
    }

    // iOS Safari uses webkitEnterFullscreen on video element
    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    } 
    // Standard fullscreen API
    else if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  };

  // Toggle Future Mode
  const toggleFutureMode = async () => {
    if (futureMode) {
      // Return to reality
      setFutureMode(false);
      return;
    }
    
    // Enter future mode - fetch prediction if not already loaded
    if (!futurePrediction) {
      setLoadingFuture(true);
      try {
        const res = await apiFetch('/api/ai/predict', {
          method: 'POST',
          body: JSON.stringify({})
        });
        const data = await res.json();
        if (res.ok) {
          setFuturePrediction(data);
        } else {
          console.error('Future prediction error:', res.status, data);
        }
      } catch (err) {
        console.error('Future prediction error:', err);
      } finally {
        setLoadingFuture(false);
      }
    }
    
    setFutureMode(true);
  };

  // Simple scoring table (same as backend scoring.js)
  const scoringTable = {
    2: { 1: 2, 2: 0 },
    3: { 1: 3, 2: 2, 3: 0 },
    4: { 1: 4, 2: 2, 3: 1, 4: 0 },
    5: { 1: 5, 2: 3, 3: 2, 4: 0, 5: 0 },
    6: { 1: 6, 2: 4, 3: 2, 4: 1, 5: 0, 6: 0 },
  };

  // Calculate points for a player in a predicted game
  const calculatePredictedPoints = (playerName, game) => {
    if (!game.players?.includes(playerName)) return 0;
    
    const playerCount = game.players.length;
    const isWinner = game.winner === playerName;
    
    // Simple: winner gets rank 1, others get rank based on position
    const rank = isWinner ? 1 : 2; // Simplified: winners get 1st, others 2nd
    
    const table = scoringTable[playerCount] || scoringTable[4];
    return table[rank] || 0;
  };

  // Get player's future games with calculated points
  const getPlayerFutureGames = (playerName) => {
    if (!futurePrediction?.games) return [];
    
    return futurePrediction.games
      .filter(game => game.players?.includes(playerName))
      .map((game, index) => ({
        gameName: game.gameName,
        players: game.players,
        winner: game.winner,
        isWinner: game.winner === playerName,
        rank: game.winner === playerName ? 1 : 2,
        points: calculatePredictedPoints(playerName, game),
        gameNumber: index + 1
      }));
  };

  // Calculate future leaderboard based on all 4 predicted games
  const getFutureLeaderboard = () => {
    if (!futureMode || !futurePrediction?.games) return leaderboard;
    
    // Calculate bonus points from all 4 games for each player
    return leaderboard.map(player => {
      const futureGames = getPlayerFutureGames(player.name);
      const bonusPoints = futureGames.reduce((sum, g) => sum + g.points, 0);
      const winsCount = futureGames.filter(g => g.isWinner).length;
      const gamesCount = futureGames.length;
      const futurePoints = player.totalPoints + bonusPoints;
      const futureTotalGames = (player.gamesPlayed || 0) + gamesCount;
      const futureAvgPointsPerGame =
        futureTotalGames > 0 ? futurePoints / futureTotalGames : null;

      return {
        ...player,
        futurePoints,
        bonusPoints,
        futureGames,
        winsCount,
        gamesCount,
        isPredictedPlayer: gamesCount > 0,
        isPredictedWinner: winsCount > 0,
        futureTotalGames,
        futureAvgPointsPerGame
      };
    });
  };

  // Leaderboard row click - expand accordion
  const handlePlayerClick = async (playerId) => {
    if (expandedPlayer === playerId) {
      setExpandedPlayer(null);
      setPlayerSessions([]);
      return;
    }
    
    setExpandedPlayer(playerId);
    setLoadingPlayerSessions(true);
    
    try {
      const res = await apiFetch(`/api/players/${playerId}/sessions?seasonId=${selectedSeasonId}`);
      const data = await res.json();
      setPlayerSessions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPlayerSessions(false);
    }
  };

  // Session click - show detail overlay
  const handleSessionClick = async (sessionId) => {
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveSession = (sessionData) => {
    fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionData)
    })
    .then(res => {
      if(res.ok) {
        setShowSessionForm(false);
        loadData();
      } else {
        alert("Virhe tallennuksessa");
      }
    });
  };

  // Group season games by date (all dates in the season)
  const groupedRecentGames = () => {
    const dateMap = {};
    recentGames.forEach(g => {
      const dateKey = new Date(g.date).toLocaleDateString('fi-FI');
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = [];
      }
      dateMap[dateKey].push(g);
    });
    
    // Get all unique dates
    const uniqueDates = Object.keys(dateMap);
    return uniqueDates.map(date => ({
      date,
      games: dateMap[date]
    }));
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const visibleLeaderboardMetrics = getOrderedLeaderboardMetrics(leaderboardSettings.visibleMetrics);
  const showAvgPointsColumn = visibleLeaderboardMetrics.includes('avgPoints');
  const leaderboardTableColSpan = 4 + visibleLeaderboardMetrics.length;
  const displayedLeaderboard = sortLeaderboardRows(
    getFutureLeaderboard(),
    leaderboardSettings.sortBy,
    leaderboardSettings.sortDirection === 'asc' ? 'asc' : 'desc',
    futureMode
  );

  if (loading && !leaderboard.length) return <div className="loading">Ladataan...</div>;

  if (showSessionForm) {
    return (
      <SessionForm 
        games={games} 
        users={users} 
        onSave={handleSaveSession} 
        onCancel={() => setShowSessionForm(false)} 
      />
    );
  }

  return (
    <div className="dashboard">
      {/* Session Detail Overlay */}
      {selectedSession && (
        <SessionDetailOverlay 
          session={selectedSession} 
          onClose={() => setSelectedSession(null)} 
        />
      )}

      {/* Fullscreen Image Overlay */}
      {fullscreenImage && (
        <div 
          className="modal-overlay" 
          onClick={() => setFullscreenImage(null)}
          style={{ cursor: 'pointer', zIndex: 3000 }}
        >
          <div 
            style={{ 
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-btn" 
              onClick={() => setFullscreenImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              <X size={20} />
            </button>
            <img 
              src={fullscreenImage} 
              alt="Session" 
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '85vh', 
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }} 
            />
          </div>
        </div>
      )}

      {/* Hero Video — collapsible; closed state is a thin reopen bar */}
      {heroCollapsed ? (
        <button
          type="button"
          className="hero-video-collapsed"
          onClick={toggleHeroCollapsed}
          aria-expanded="false"
        >
          <ChevronDown size={18} />
          <span>Video</span>
        </button>
      ) : (
        <div className="hero-video-wrap">
          <button
            type="button"
            className="hero-video-collapse-btn"
            onClick={toggleHeroCollapsed}
            aria-label="Piilota video"
            aria-expanded="true"
          >
            <ChevronUp size={18} />
            <span>Piilota</span>
          </button>
          <div className="hero-video" onClick={handleVideoFullscreen}>
            <video 
              ref={videoRef}
              autoPlay 
              muted 
              playsInline 
              poster="/video-poster.jpg"
              onEnded={handleVideoEnded}
              src={videoPlaylist[currentVideoIndex]}
            />
            <div className="hero-overlay">
              <h1>Let's Play!</h1>
            </div>
          </div>
        </div>
      )}

      {/* Season indicator with selector */}
      <div className="season-indicator-container">
        <div className="season-indicator">
          <Calendar size={16} />
          <span>Kausi {selectedSeasonLabel || '—'}</span>
          {selectedSeasonId != null && !isCurrentSeason && (
            <span className="historical-badge">Historiallinen</span>
          )}
        </div>
        
        {availableSeasons.length > 1 && (
          <div className="season-selector-wrapper">
            <button 
              className="btn-secondary season-btn"
              onClick={() => setShowSeasonSelector(!showSeasonSelector)}
            >
              <History size={16} />
              <span>Muut kaudet</span>
            </button>
            
            {showSeasonSelector && (
              <div className="season-popover">
                <div className="season-popover-header">Valitse kausi</div>
                <ul className="season-list">
                  {availableSeasons.map(season => (
                    <li 
                      key={season.id}
                      className={`season-option ${season.id === selectedSeasonId ? 'selected' : ''}`}
                      onClick={() => handleSeasonChange(season.id)}
                    >
                      <Calendar size={14} />
                      <span>Kausi {getSeasonLabel(season)}</span>
                      {currentSeasonMeta && season.id === currentSeasonMeta.id && (
                        <span className="current-badge">Nykyinen</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Oracle Bar */}
      <NextGamePrediction />

      {/* Smart Product Recommendations */}
      <div style={{ padding: '0 1rem' }}>
        <SmartRecommendations 
          placement="dashboard_featured" 
          userId={user?.id}
          variant="card"
          maxProducts={3}
        />
      </div>

      {/* Upcoming Events Widget */}
      {gameNightsEnabled && upcomingEvents.length > 0 && (
        <div className="upcoming-events-widget">
          <div className="widget-header">
            <h3><Calendar size={18} /> Tulevat peli-illat</h3>
            <Link to="/events" className="btn-link">Näytä kaikki →</Link>
          </div>
          <div className="events-preview">
            {upcomingEvents.slice(0, 3).map(event => (
              <Link key={event.id} to="/events" className="event-preview-card">
                <div className="event-preview-info">
                  <span className="event-preview-title">{event.title}</span>
                  {event.gameName && <span className="event-preview-game">{event.gameName}</span>}
                </div>
                <div className="event-preview-meta">
                  {event.confirmedDate ? (
                    <span className="event-confirmed">
                      <Calendar size={14} />
                      {new Date(event.confirmedDate).toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  ) : (
                    <span className="event-voting">
                      <Clock size={14} />
                      Äänestys ({event.timeSlotCount || 0} aikaa)
                    </span>
                  )}
                  <span className="event-attendees">
                    <Users size={14} />
                    {event.attendeeCount || 0}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid">
        {/* Interactive Leaderboard */}
        <div className={`card leaderboard ${futureMode ? 'future-mode' : ''}`}>
          <div className="leaderboard-header">
            <h3>
              <Trophy className="icon" /> 
              {futureMode ? 'Leaderboard (Ennuste)' : 'Leaderboard'}
            </h3>
            {/* Only show future prediction for current season */}
            {isCurrentSeason && (
              <button 
                type="button"
                className={`btn-future ${futureMode ? 'active' : ''} ${loadingFuture ? 'loading' : ''}`}
                onClick={toggleFutureMode}
                disabled={loadingFuture}
                title={
                  futureMode && !visibleLeaderboardMetrics.includes('points')
                    ? 'Menestyspisteet-sarake on piilotettu; ennuste näkyy Pelit- ja Ø MP -sarakkeissa sekä rivin laajennuksessa'
                    : undefined
                }
              >
                <span className={loadingFuture ? 'hourglass-spin' : ''}>
                  {loadingFuture ? '⏳' : '🔮'}
                </span>
                {loadingFuture ? 'Analysoidaan...' : futureMode ? 'Palaa todellisuuteen' : 'Näytä tulevaisuus'}
              </button>
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th>Sija</th>
                <th>Nimi</th>
                {showAvgPointsColumn ? (
                  <th
                    key="avgPoints-leading"
                    className="leaderboard-avg-points-col"
                    title={futureMode ? 'Menestyspisteet / peli (mukana ennustetut pelit)' : undefined}
                  >
                    Ø MP / peli{futureMode ? ' *' : ''}
                  </th>
                ) : (
                  <th key="games-leading" title={futureMode ? 'Pelatut + ennustetut pelit' : undefined}>
                    Pelit
                  </th>
                )}
                {visibleLeaderboardMetrics.map((metric, midx) => {
                  const mc = leaderboardMetricMobileClass(midx);
                  if (metric === 'avgPoints') {
                    return (
                      <th
                        key="games"
                        className={mc}
                        title={futureMode ? 'Pelatut + ennustetut pelit' : undefined}
                      >
                        Pelit
                      </th>
                    );
                  }
                  if (metric === 'time') {
                    return <th key={metric} className="hide-mobile-1">Aika</th>;
                  }
                  if (metric === 'points') {
                    return (
                      <th key={metric}>
                        <span className="hide-mobile-2">{futureMode ? 'Ennuste' : 'Menestyspisteet'}</span>
                        <span className="show-mobile-only">MP</span>
                      </th>
                    );
                  }
                  if (metric === 'winRate') {
                    return <th key={metric} className={mc}>Voitto-%</th>;
                  }
                  return null;
                })}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayedLeaderboard.map((s, i) => (
                <React.Fragment key={s.id}>
                  <tr 
                    onClick={() => handlePlayerClick(s.id)} 
                    className={`clickable-row ${expandedPlayer === s.id ? 'expanded' : ''} ${futureMode && s.isPredictedWinner ? 'future-winner' : ''} ${futureMode && s.isPredictedPlayer ? 'future-player' : ''}`}
                  >
                  <td>{i + 1}.</td>
                    <td>
                      {s.name}
                      {futureMode && s.isPredictedWinner && <span className="winner-badge">👑</span>}
                    </td>
                  {showAvgPointsColumn ? (
                    <td key="avgPoints-leading" className="leaderboard-avg-points-col">
                      {(() => {
                        const avg =
                          futureMode && s.futureAvgPointsPerGame != null
                            ? s.futureAvgPointsPerGame
                            : s.avgPointsPerGame;
                        return avg != null && !Number.isNaN(Number(avg))
                          ? parseFloat(avg).toFixed(2)
                          : '—';
                      })()}
                    </td>
                  ) : (
                    <td key="games-leading">
                      {futureMode && s.futureTotalGames != null ? s.futureTotalGames : s.gamesPlayed}
                    </td>
                  )}
                    {visibleLeaderboardMetrics.map((metric, midx) => {
                      const mc = leaderboardMetricMobileClass(midx);
                      if (metric === 'avgPoints') {
                        return (
                          <td key="games" className={mc}>
                            {futureMode && s.futureTotalGames != null ? s.futureTotalGames : s.gamesPlayed}
                          </td>
                        );
                      }
                      if (metric === 'time') {
                        return <td key={metric} className="hide-mobile-1">{formatDuration(s.totalPlaytime)}</td>;
                      }
                      if (metric === 'points') {
                        return (
                          <td key={metric}>
                            {futureMode ? (
                              <span className="future-points">
                                {s.futurePoints}
                                {s.bonusPoints > 0 && <span className="bonus">+{s.bonusPoints}</span>}
                              </span>
                            ) : (
                              s.totalPoints
                            )}
                          </td>
                        );
                      }
                      if (metric === 'winRate') {
                        return (
                          <td key={metric} className={mc}>
                            {s.winRate != null && !Number.isNaN(Number(s.winRate))
                              ? `${Number(s.winRate).toFixed(1)}%`
                              : '—'}
                          </td>
                        );
                      }
                      return null;
                    })}
                    <td>
                      {expandedPlayer === s.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </td>
                  </tr>
                  {/* Expanded Player Sessions */}
                  {expandedPlayer === s.id && (
                    <tr className="accordion-row">
                      <td colSpan={leaderboardTableColSpan}>
                        <div className="player-sessions">
                          {/* Future Games Section (when in future mode) */}
                          {futureMode && s.futureGames?.length > 0 && (
                            <div className="future-games-section">
                              <div className="future-games-header">
                                <span>🔮 Ennustetut pelit</span>
                              </div>
                              <table className="sub-table future-table">
                                <thead>
                                  <tr><th>#</th><th>Peli</th><th>Sija</th><th>MP</th><th>Pelaajat</th></tr>
                                </thead>
                                <tbody>
                                  {s.futureGames.map((fg, idx) => (
                                    <tr 
                                      key={`future-${idx}`} 
                                      className={`future-game-row ${fg.isWinner ? 'is-winner' : ''}`}
                                    >
                                      <td className="game-number-cell">
                                        <span className="game-number-badge">{fg.gameNumber}</span>
                                      </td>
                                      <td>
                                        {fg.gameName}
                                        {fg.isWinner && <span className="winner-star">⭐</span>}
                                      </td>
                                      <td>{fg.rank}.</td>
                                      <td>
                                        <span className="mp-badge future-mp">
                                          <Medal size={14} />
                                          <span>+{fg.points}</span>
                                        </span>
                                      </td>
                                      <td className="players-cell">{fg.players?.join(', ')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div className="future-summary">
                                Yhteensä: <strong>+{s.bonusPoints} MP</strong> ({s.winsCount} voittoa, {s.gamesCount} peliä)
                              </div>
                            </div>
                          )}
                          
                          {/* Divider between future and past */}
                          {futureMode && s.futureGames?.length > 0 && playerSessions.length > 0 && (
                            <div className="section-divider">
                              <span>📜 Pelatut pelit</span>
                            </div>
                          )}
                          
                          {/* Past Sessions */}
                          {loadingPlayerSessions ? (
                            <div className="loading-small">Ladataan...</div>
                          ) : playerSessions.length === 0 && !futureMode ? (
                            <p className="no-sessions">Ei pelejä tällä kaudella</p>
                          ) : playerSessions.length > 0 && (
                            <table className="sub-table">
                              <thead>
                                <tr><th></th><th>Peli</th><th>Sija</th><th>VP</th><th>MP</th><th>Kesto</th></tr>
                              </thead>
                              <tbody>
                                {playerSessions.map((ps, idx) => (
                                  <tr 
                                    key={`${ps.sessionId}-${idx}`} 
                                    onClick={(e) => { e.stopPropagation(); handleSessionClick(ps.sessionId); }}
                                    className="clickable-row sub-row"
                                  >
                                    <td className="session-thumb-cell">
                                      {ps.sessionImage ? (
                                        <img 
                                          src={ps.sessionImage} 
                                          alt="" 
                                          className="mini-thumb"
                                          onClick={(e) => { e.stopPropagation(); setFullscreenImage(ps.sessionImage); }}
                                        />
                                      ) : ps.gameImage ? (
                                        <img 
                                          src={ps.gameImage} 
                                          alt="" 
                                          className="mini-thumb"
                                          onError={(e) => { e.target.onerror = null; e.target.src = '/empty-game.jpg'; }}
                                          onClick={(e) => { e.stopPropagation(); setFullscreenImage(ps.gameImage); }}
                                        />
                                      ) : null}
                                    </td>
                                    <td>{ps.gameName}</td>
                                    <td>{ps.rank}.</td>
                                    <td className="raw-score">{ps.rawScore ?? '-'}</td>
                                    <td>
                                      <span className="mp-badge">
                                        <Medal size={14} />
                                        <span>+{ps.leaguePoints ?? 0}</span>
                                      </span>
                                    </td>
                                    <td>{formatDuration(ps.durationMinutes)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Season Games Grouped by Date */}
        <div className="card recent-games">
          <h3>Kauden {selectedSeasonLabel || '—'} pelit</h3>
          {groupedRecentGames().map(group => (
            <div key={group.date} className="date-group">
              <div className="date-header">
                <Calendar size={14} /> {group.date}
              </div>
              <ul>
                {group.games.map(g => (
                  <li 
                    key={g.id} 
                    onClick={() => handleSessionClick(g.id)}
                    className="clickable-row"
                  >
                    <div className="recent-game-row">
                      <div className="game-info">
                        <strong>{g.game}</strong>
                        <span className="winner-info">
                          <Trophy size={12} /> {g.winner}
                          {g.winnerRawScore && <span className="raw-score-inline"> ({g.winnerRawScore} VP)</span>}
                          <span className="mp-badge">
                            <Medal size={12} />
                            <span>+{g.winnerLeaguePoints ?? 0}</span>
                          </span>
                        </span>
                      </div>
                      <div className="game-meta">
                        <Clock size={12} /> {formatDuration(g.durationMinutes)}
                      </div>
                    </div>
              </li>
            ))}
          </ul>
        </div>
          ))}
    </div>
      </div>
      
      <button className="fab" onClick={() => setShowSessionForm(true)}>
        <Plus size={32} />
      </button>
    </div>
  );
};

// --- MAIN APP ---
export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showTerminalGame, setShowTerminalGame] = useState(false);
  const [appGames, setAppGames] = useState([]);
  const [appPlayers, setAppPlayers] = useState([]);

  // Restore session only if JWT is still valid; otherwise clear and show landing/login
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const savedUser = getStoredUser();
      const hasClubKey = !!(savedUser?.club?.apiKey || getClubKey());
      const tokenOk = isAccessTokenValid();

      if (!savedUser || !hasClubKey || !tokenOk) {
        if (savedUser || getAccessToken() || getClubKey()) {
          console.log('[App] Stale or expired session cleared');
          clearAuth();
        }
        if (!cancelled) {
          setUser(null);
          resetClubTheme();
          setAuthReady(true);
        }
        return;
      }

      if (savedUser?.club?.apiKey) {
        localStorage.setItem('masaklubi_club_key', savedUser.club.apiKey);
      }

      // Confirm token still accepted by server before showing club UI
      try {
        const res = await apiFetch('/api/club/current');
        if (cancelled) return;
        if (!res.ok) {
          console.log('[App] Session rejected by server, clearing auth');
          clearAuth();
          setUser(null);
          resetClubTheme();
        } else {
          const club = await res.json();
          const next = {
            ...savedUser,
            club: {
              ...savedUser.club,
              id: club.id,
              name: club.name,
              slug: club.slug,
              description: club.description,
              primary_color: club.primary_color,
              language: club.language,
              logo_url: club.logo_url,
              hero_video_url: club.hero_video_url,
              apiKey: savedUser.club?.apiKey
            }
          };
          storeUser(next);
          setUser(next);
          applyClubTheme(next.club);
        }
      } catch (err) {
        console.warn('[App] Session verify failed:', err);
        if (!cancelled) {
          // Network error: keep local session only if token still looks valid
          if (isAccessTokenValid()) {
            setUser(savedUser);
            applyClubTheme(savedUser.club);
          } else {
            clearAuth();
            setUser(null);
            resetClubTheme();
          }
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  // apiFetch clears storage on 401 — sync React state back to logged-out UI
  useEffect(() => {
    const onExpired = () => {
      console.log('[App] Auth expired event — returning to landing');
      setUser(null);
      resetClubTheme();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // Refresh club branding (name, description, primary_color) from API
  useEffect(() => {
    if (!authReady || !user || !getClubKey() || !isAccessTokenValid()) return;

    let cancelled = false;
    apiFetch('/api/club/current')
      .then((res) => (res.ok ? res.json() : null))
      .then((club) => {
        if (cancelled || !club) return;
        applyClubTheme(club);
        setUser((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            club: {
              ...prev.club,
              id: club.id,
              name: club.name,
              slug: club.slug,
              description: club.description,
              primary_color: club.primary_color,
              language: club.language,
              logo_url: club.logo_url,
              hero_video_url: club.hero_video_url,
              apiKey: prev.club?.apiKey
            }
          };
          storeUser(next);
          return next;
        });
      })
      .catch((err) => console.warn('[App] Club branding fetch failed:', err));

    return () => {
      cancelled = true;
    };
  }, [authReady, user?.club?.id, user?.isImpersonating]);

  // Fetch games and players for event scheduling (using authenticated API)
  useEffect(() => {
    if (authReady && user && isAccessTokenValid()) {
      Promise.all([
        apiFetch('/api/games').then(r => r.ok ? r.json() : []),
        apiFetch('/api/users').then(r => r.ok ? r.json() : [])
      ]).then(([games, players]) => {
        setAppGames(games);
        setAppPlayers(players);
      }).catch(err => console.error('App data fetch error:', err));
    }
  }, [authReady, user]);

  // Handle login - stores user AND club API key
  const handleLogin = useCallback((userData) => {
    setUser(userData);
    storeUser(userData); // This also stores the club API key
    applyClubTheme(userData.club);
    console.log('[App] User logged in, club key stored');
  }, []);

  // Handle logout - clears ALL auth data including club API key
  const handleLogout = useCallback(() => {
    setUser(null);
    clearAuth(); // Clears both user and club key
    resetClubTheme();
    console.log('[App] User logged out, auth cleared');
  }, []);

  // After Club Settings save: update live branding + stored user.club
  const handleClubUpdated = (club) => {
    if (!club) return;
    applyClubTheme(club);
    setUser((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        club: {
          ...prev.club,
          ...club,
          apiKey: prev.club?.apiKey || club.apiKey
        }
      };
      storeUser(next);
      return next;
    });
  };

  // Handle SuperAdmin impersonating a club
  const handleLoginAsClub = async (clubId) => {
    try {
      // Store original SuperAdmin session before impersonating
      const originalUser = getStoredUser();
      const originalToken = localStorage.getItem('masaklubi_access_token');
      const originalClubKey = localStorage.getItem('masaklubi_club_key');
      
      if (originalUser && originalUser.role === 'superadmin') {
        localStorage.setItem('masaklubi_superadmin_original', JSON.stringify({
          user: originalUser,
          token: originalToken,
          clubKey: originalClubKey
        }));
      }
      
      const response = await apiFetch(`/api/superadmin/impersonate/${clubId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        alert(`Klubiin kirjautuminen epäonnistui: ${error.error || 'Unknown error'}`);
        return;
      }
      
      const userData = await response.json();
      
      // Store the new session (this switches context to the target club)
      setUser(userData);
      storeUser(userData);
      applyClubTheme(userData.club);
      
      console.log(`[App] SuperAdmin impersonating club: ${userData.club?.name}`);
      
      // Navigate to dashboard of the impersonated club
      window.location.href = '/';
      
    } catch (err) {
      console.error('[App] Impersonation error:', err);
      alert('Klubiin kirjautuminen epäonnistui');
    }
  };

  const launchTerminalGame = () => {
    setShowTerminalGame(true);
  };

  return (
    <Router>
      <div className="app-container">
        {authReady && <MagicLinkAuth onLogin={handleLogin} onClearUser={handleLogout} />}
        {!authReady ? (
          <div className="auth-boot-loading" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
            Ladataan...
          </div>
        ) : user ? (
          <>
            <LocationTracker />
            <InitialRedirect user={user} />
            <Navigation user={user} onLogout={handleLogout} />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Dashboard user={user} />} />
                <Route path="/games" element={<GamesLibrary user={user} />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/events" element={<EventScheduler user={user} games={appGames} players={appPlayers} />} />
                <Route path="/suggestions" element={<SuggestionBox user={user} />} />
                {user.role === 'board' && (
                  <>
                    <Route path="/admin" element={<AdminPanel user={user} />} />
                    <Route path="/admin/results" element={<ResultsMaintenance />} />
                  </>
                )}
                {user.role === 'admin' && (
                  <>
                    <Route path="/admin" element={<AdminPanel user={user} />} />
                    <Route path="/admin/scoring" element={<ScoringSettings />} />
                    <Route path="/admin/results" element={<ResultsMaintenance />} />
                    <Route path="/admin/settings" element={<AdminSettings user={user} />} />
                    <Route path="/admin/ads" element={<AdsAdmin />} />
                    <Route path="/admin/club" element={<ClubSettings user={user} onClubUpdated={handleClubUpdated} />} />
                  </>
                )}
                {user.role === 'superadmin' && (
                  <>
                    <Route path="/superadmin" element={<SuperAdminProtected user={user} onLoginAsClub={handleLoginAsClub} />} />
                    {/* SuperAdmin has access to all admin routes too */}
                    <Route path="/admin" element={<AdminPanel user={user} />} />
                    <Route path="/admin/scoring" element={<ScoringSettings />} />
                    <Route path="/admin/results" element={<ResultsMaintenance />} />
                    <Route path="/admin/settings" element={<AdminSettings user={user} />} />
                    <Route path="/admin/ads" element={<AdsAdmin />} />
                    <Route path="/admin/club" element={<ClubSettings user={user} onClubUpdated={handleClubUpdated} />} />
                  </>
                )}
              </Routes>
            </main>
            
            {/* The Oracle Chat Interface */}
            <ChatInterface user={user} onLaunchGame={launchTerminalGame} />
            
            {/* Hitchhiker's Guide Terminal Game (Easter Egg) */}
            {showTerminalGame && (
              <TerminalGame 
                user={user} 
                onClose={() => setShowTerminalGame(false)} 
              />
            )}
          </>
        ) : (
          <Routes>
             <Route path="/" element={<HomeOrLogin />} />
             <Route path="/onboarding" element={<OnboardingWizard email="" verificationCode="" onComplete={() => {}} />} />
             <Route path="*" element={<HomeOrLogin />} />
          </Routes>
        )}
      </div>
    </Router>
  );
}
